import React, { forwardRef, useState } from 'react';

import { SvgIcon } from '@mui/material';
import { AlineTooltip, SelectButton } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import type { IconVariety } from './ColorSelectComponent';
import { TRANSPARENT_HSLA } from './ColorSelectComponent';

const useStyles = makeStyles()(theme => ({
  button: {
    height: 32,
    margin: theme.spacing(0, 0.25),
    width: 32,
  },
  colorBarIcon: {
    height: 3,
    width: 24,
  },
  iconWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface ColorSelectButtonProps {
  iconType: IconVariety;
  menuOpen: boolean;
  selectedColor: string | null;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tooltipTitle: string;
  disabled?: boolean;
}

function getIconPath(iconsType: IconVariety): JSX.Element {
  switch (iconsType) {
    case 'A':
      return (
        <path d="M8.179 11.676h7.071V8.143c0-1.953-1.583-3.536-3.536-3.536-1.952 0-3.535 1.583-3.535 3.536v3.533zm7.071 1.5H8.179v3.643h.107c.414 0 .75.336.75.75s-.336.75-.75.75H6.57c-.414 0-.75-.336-.75-.75s.336-.75.75-.75h.108V8.143c0-2.781 2.254-5.036 5.035-5.036s5.036 2.255 5.036 5.036v8.676h.107c.414 0 .75.336.75.75s-.336.75-.75.75h-.798c-.02.002-.04.002-.059.002-.02 0-.04 0-.059-.002h-.798c-.414 0-.75-.336-.75-.75s.336-.75.75-.75h.107v-3.643z" />
      );
    case 'marker':
      return (
        <path d="M8.647 16.662l.045.04.24-.12c.755-.377 1.645-.377 2.4 0 .145.073.32.045.433-.069l.489-.49-4.509-4.509-.487.488c-.115.115-.143.29-.07.437.378.756.378 1.645 0 2.402l-.12.241 1.579 1.58zm-.464 1.614l-.526.526c-.14.141-.331.22-.53.22H3.752c-.669 0-1.003-.808-.53-1.28l2.235-2.236c-.077-.195-.07-.417.027-.612l.363-.724c.166-.334.166-.727 0-1.059-.364-.723-.222-1.6.35-2.17L7.21 9.928l.032-.03.005-.005 6.763-6.116c1.185-1.074 3.003-1.03 4.135.1l1.746 1.747c1.13 1.131 1.174 2.95.1 4.133l-6.117 6.764-.004.004-.03.031-1.014 1.017c-.57.57-1.442.712-2.164.352-.334-.167-.726-.167-1.06 0l-.726.362c-.223.112-.481.104-.693-.011zm-1.082-1.039l-.627-.627-.912.912h1.254l.285-.285zm1.732-6.756l4.454 4.453 5.593-6.183c.536-.593.514-1.502-.05-2.067l-1.745-1.746c-.566-.564-1.475-.586-2.068-.05l-6.184 5.593z" />
      );
    case 'fill':
      return (
        <path
          clipRule="evenodd"
          d="M5.88084 9.70482V5.82031C5.88084 4.16346 7.22398 2.82031 8.88084 2.82031C10.359 2.82031 11.5874 3.88933 11.8352 5.2964C12.0107 5.37707 12.1751 5.48967 12.3196 5.63419L18.8385 12.1545C19.1675 12.4842 19.2786 12.9732 19.1244 13.4127C18.9702 13.8521 18.578 14.1645 18.1145 14.2165L16.3877 14.4086L12.32 18.4763C11.6588 19.1384 10.7615 19.5104 9.82584 19.5104C8.89015 19.5104 7.99286 19.1384 7.33193 18.4766L4.71475 15.8586C4.05298 15.1975 3.68115 14.3004 3.68115 13.3649C3.68115 12.4295 4.05298 11.5324 4.71443 10.8716L5.88084 9.70482ZM10.3808 7.32515V12.0078C10.3808 12.422 10.7166 12.7578 11.1308 12.7578C11.545 12.7578 11.8808 12.422 11.8808 12.0078V7.31686L17.3545 12.7918L15.9604 12.9469C15.7912 12.9657 15.6334 13.0416 15.513 13.162L11.259 17.416C10.8788 17.7967 10.3634 18.0104 9.82584 18.0104C9.28831 18.0104 8.77285 17.7967 8.39301 17.4163L5.77524 14.7978C5.39476 14.4177 5.18115 13.9023 5.18115 13.3649C5.18115 12.8276 5.39476 12.3122 5.77492 11.9324L10.3808 7.32515ZM10.3091 5.36072C10.115 4.75711 9.54895 4.32031 8.88084 4.32031C8.05241 4.32031 7.38084 4.99189 7.38084 5.82031V8.20439L9.9503 5.63419C10.0598 5.52471 10.1807 5.43356 10.3091 5.36072ZM18.8524 15.6099C18.576 15.0571 17.7872 15.0571 17.5108 15.6099L16.2703 18.0909C15.8589 18.9137 16.0205 19.9074 16.6713 20.5575C17.071 20.958 17.6148 21.1836 18.182 21.1836C18.7491 21.1836 19.2929 20.958 19.6935 20.5565C20.3433 19.9069 20.5044 18.9132 20.0928 18.0906L18.8524 15.6099ZM17.7323 19.4971C17.5375 19.3026 17.4894 19.0067 17.6119 18.7617L18.1816 17.6224L18.7513 18.7617C18.874 19.0069 18.826 19.3028 18.6323 19.4964C18.5125 19.6165 18.3507 19.6836 18.182 19.6836C18.0132 19.6836 17.8514 19.6165 17.7323 19.4971Z"
          fillRule="evenodd"
        />
      );
    case 'line':
      return (
        <path
          clip-rule="evenodd"
          d="M20.374 7.34836L14.2021 15.7699C14.1141 15.8901 13.9922 15.9813 13.8521 16.0319L10.4284 17.2692C9.92222 17.4521 9.39382 17.0581 9.42476 16.5207L9.64087 12.7667C9.64919 12.6222 9.69913 12.4832 9.78467 12.3665L15.9543 3.94699C16.8716 2.68958 18.6129 2.43896 19.8478 3.4077C21.0429 4.34647 21.2783 6.10694 20.374 7.34836ZM18.8992 4.56974L18.9059 4.57496C18.3581 4.14532 17.5852 4.25655 17.1652 4.83231L11.1256 13.0742L10.9876 15.4722L13.1274 14.6989L19.1622 6.46442C19.5923 5.87297 19.4788 5.02529 18.8992 4.56974ZM16.25 16.4388C16.25 16.8531 16.5858 17.1888 17 17.1888H19.2326C19.398 17.2328 19.5464 17.3391 19.6875 17.5305V19.6632C19.5776 19.6793 19.4488 19.6888 19.3125 19.6888H4.6875C4.55119 19.6888 4.42241 19.6793 4.3125 19.6632V17.2145C4.42241 17.1983 4.55119 17.1888 4.6875 17.1888H6.91443C7.32864 17.1888 7.66443 16.8531 7.66443 16.4388C7.66443 16.0246 7.32864 15.6888 6.91443 15.6888H4.6875C3.63582 15.6888 2.8125 15.9434 2.8125 16.7867V20.091C2.8125 20.9343 3.63582 21.1888 4.6875 21.1888H19.3125C20.3642 21.1888 21.1875 20.9343 21.1875 20.091V17.3084C21.1875 17.1748 21.1518 17.0436 21.0841 16.9285C20.6878 16.2539 20.134 15.8259 19.4466 15.7009L19.3125 15.6888H17C16.5858 15.6888 16.25 16.0246 16.25 16.4388Z"
          fill-rule="evenodd"
        />
      );
  }
}

export const ColorSelectButton = forwardRef(function ColorSelectButton(
  props: ColorSelectButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const { classes } = useStyles();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const { disabled, iconType, menuOpen, selectedColor, setMenuOpen, tooltipTitle } = props;

  function toggleMenu(): void {
    setMenuOpen(!menuOpen);
  }

  function handleTooltipOpen(): void {
    setTooltipOpen(true);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(false);
  }

  return (
    <AlineTooltip
      arrow
      open={tooltipOpen && !menuOpen}
      placement="bottom"
      title={tooltipTitle}
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
    >
      <div>
        <SelectButton
          ref={ref}
          className={classes.button}
          disabled={disabled}
          selected={menuOpen}
          onClick={toggleMenu}
        >
          <div className={classes.iconWrapper}>
            <SvgIcon viewBox="0 0 24 24">{getIconPath(iconType)}</SvgIcon>
            <SvgIcon className={classes.colorBarIcon} viewBox="0 0 24 3">
              <rect
                fill={selectedColor ?? TRANSPARENT_HSLA}
                fillRule="evenodd"
                height="3"
                rx="1.5"
                width="20"
                x="2"
              />
            </SvgIcon>
          </div>
        </SelectButton>
      </div>
    </AlineTooltip>
  );
});
